import React from 'react'
import { color, motion, PENDING_SEAL_DOT_PX, prefersReducedMotion } from './verificationTheme'
import { verificationGlyph } from '../player/designPreview'

// The verification control-bar glyph (a V badge): opens the verification modal. Grey while
// everything passes, alarm (with a slow pulse) the moment a mismatch is
// found. White at rest, the skin's green while its window is open.
// The plan (T6) said never green, so that the V could not read as a
// "verified" badge; Gavin's 30 Sep rule that the button whose menu is open
// is green applies to this one too (Kai, 30 Sep), so it wears the skin's
// open colour ONLY while its own window is up — and alarm still wins.
//
// Styled to match the weight and 40×40 hit area of the other player controls
// (`player/styles.ts`'s `button`), but kept self-contained here rather than
// importing that stylesheet, since this glyph's colour comes from
// `verificationTheme` rather than the player's own palette.

/** The glyph, as supplied: a silhouette on transparency, used as a
 * mask so it takes the state colour. It was a V badge (Gavin, 10 Sep, redrawn
 * 30 Sep), then two chain links (8 Oct, diagonal 11 Oct), then the
 * FileFidelity logo (16 Oct); since 17 Oct it is the V again, redrawn.
 * Art and size come from `designPreview`, which keeps the logo as a variant. */
const { image: GLYPH_MASK, size: GLYPH_SIZE } = verificationGlyph()

const pulseKeyframes = `
@keyframes verification-glyph-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
}
`

let injectedPulseStyle = false
function ensurePulseStyleInjected() {
  if (injectedPulseStyle || typeof document === 'undefined') return
  injectedPulseStyle = true
  const style = document.createElement('style')
  style.setAttribute('data-verification-glyph-pulse', '')
  style.textContent = pulseKeyframes
  document.head.appendChild(style)
}

export interface VerificationGlyphProps {
  /** Whether the verification modal this glyph opens is currently open. */
  open: boolean
  /** True once a mismatch has been found. Overrides every other colour. */
  failed: boolean
  /** The skin's colour for a bar button whose menu is open. */
  openColor: string
  /**
   * Verified against the chain, but the record is not sealed yet — the
   * publisher can still replace it. Adds a small informational dot to the V
   * and nothing else: the V keeps its colour, and a mismatch hides the dot,
   * because alarm says everything that needs saying.
   */
  pendingSeal?: boolean
  onClick: () => void
}

export default function VerificationGlyph({ open, failed, openColor, pendingSeal = false, onClick }: VerificationGlyphProps) {
  ensurePulseStyleInjected()

  const reducedMotion = prefersReducedMotion()
  const pulse = failed && !reducedMotion

  // Pure white at rest, not the register's bone: Gavin, 30 Sep evening, "the
  // V is grey - can it be white?" (bone at 0.72 read as grey in the bar).
  const glyphColor = failed ? color.alarm : open ? openColor : '#FFFFFF'
  const showPending = pendingSeal && !failed
  const label = failed
    ? 'Verification failed — open details'
    : showPending
      ? 'Verification — verified, not sealed yet'
      : 'Verification'

  return (
    <button
      type="button"
      data-player-controls
      aria-label={label}
      aria-expanded={open}
      onClick={onClick}
      style={{
        position: 'relative',
        backgroundColor: 'transparent',
        color: glyphColor,
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '40px',
        minHeight: '40px',
        opacity: 1,
        animation: pulse ? `verification-glyph-pulse ${motion.glyphPulseMs}ms ease-in-out infinite` : undefined,
      }}
    >
      {/* Drawn as a stencil, not an image. The artwork is a plain silhouette
          and carries no colour of its own, so the mask supplies the shape and
          `backgroundColor` supplies the state — which is the whole point: this
          glyph MUST be able to turn alarm the moment a mismatch is found, and
          an <img> could not. */}
      <span
        aria-hidden="true"
        style={{
          display: 'block',
          width: GLYPH_SIZE,
          height: GLYPH_SIZE,
          backgroundColor: glyphColor,
          WebkitMaskImage: `url(${GLYPH_MASK})`,
          maskImage: `url(${GLYPH_MASK})`,
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskPosition: 'center',
          WebkitMaskSize: 'contain',
          maskSize: 'contain',
        }}
      />
      {showPending && (
        <span
          aria-hidden="true"
          data-pending-seal
          style={{
            position: 'absolute',
            top: 7,
            right: 7,
            width: PENDING_SEAL_DOT_PX,
            height: PENDING_SEAL_DOT_PX,
            borderRadius: '50%',
            backgroundColor: color.pending,
            // A dark rim keeps the dot legible where it overlaps the V.
            boxShadow: `0 0 0 1.5px ${color.ink}`,
          }}
        />
      )}
    </button>
  )
}
